/**
 * ログイン状態に応じて、ログイン画面 / 本体 / 案内画面を出し分ける。
 * users/{uid} が無い(=管理者がまだ権限を設定していない)ケースを明示的に扱う。
 */
import type { ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import LoginPage from './LoginPage';

function Notice({ title, body, onSignOut }: { title: string; body: string; onSignOut: () => void }) {
  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="brand">CC DIVISION</div>
        <h1>{title}</h1>
        <p className="hint">{body}</p>
        <button className="btn-primary" onClick={onSignOut}>
          ログアウト
        </button>
      </div>
    </div>
  );
}

export default function AuthGate({ children }: { children: ReactNode }) {
  const { status, signOut, firebaseUser, retryProfile } = useAuth();

  if (status === 'loading') {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="brand">CC DIVISION</div>
          <h1>読み込み中…</h1>
        </div>
      </div>
    );
  }

  if (status === 'signed-out') return <LoginPage />;

  // 権限の読込が通信などで失敗したとき(2026-10-05 全端末対応。以前は「読み込み中…」のまま止まっていた)
  if (status === 'error') {
    return (
      <div className="login-screen">
        <div className="login-card" role="alert">
          <div className="brand">CC DIVISION</div>
          <h1>通信できませんでした</h1>
          <p className="hint">利用権限を読み込めませんでした。通信状況を確認して、もう一度お試しください。</p>
          <button type="button" className="btn-primary" onClick={() => void retryProfile()}>
            もう一度読み込む
          </button>
          <button type="button" className="login-link" onClick={() => void signOut()}>
            ログアウト
          </button>
        </div>
      </div>
    );
  }

  if (status === 'no-profile') {
    return (
      <Notice
        title="権限が未設定です"
        body={`${firebaseUser?.email ?? 'このアカウント'} にはまだ利用権限が設定されていません。管理者にユーザー追加を依頼するか、登録したGoogleアカウントでログインし直してください。`}
        onSignOut={() => void signOut()}
      />
    );
  }

  if (status === 'inactive') {
    return (
      <Notice
        title="利用が停止されています"
        body="このアカウントは無効化されています。管理者に連絡してください。"
        onSignOut={() => void signOut()}
      />
    );
  }

  return <>{children}</>;
}
